/**
 * V17.523 (Phase 1, brief 2026-10-26-002): the one sidebar's rules, without React
 * (design record 2026-10-25-050, Rules 2–4 and 7).
 *
 *  - canSee: an item shows only if the user passes ITS ROUTE'S guard — the registry gate equals
 *    the route guard (B1 / AC-15), so the menu never offers a page that refuses the user.
 *  - visibleSections: sections in registry order, each with ≥1 visible item. Home is separate
 *    and always shown.
 *  - activeItemPath: exact match (incl. #hash) wins, else the longest visible prefix at a "/"
 *    boundary; Home only on exactly /dashboard.
 *  - firstVisitOpen: if the user has a main section (a visible item whose primaryForRoles names one
 *    of their roles), ONLY that section opens — the current page's section stays closed with its blue
 *    dot (resolutions 2026-10-26-010 / -011, VOD B9). Otherwise rule 4: ≤12 visible items → all
 *    sections open; else only the current page's section.
 *  - load/saveNavState: localStorage `vtw.nav.v1.<userId>` = { collapsed, open[] }.
 *  - searchItems: part of the Lao or English label, visible items only.
 */
import { HOME, NAV_SECTIONS, type NavItem, type NavSection, type NavSectionId } from '../../app/navigation';

export interface NavUser {
  permissions: readonly string[];
  /** Role names the user is assigned (active role + all_role_names), as hasRoleAssignment reads them. */
  roleAssignments: readonly string[];
  /** The active role enum (user.role); used by the excludeScouts test, like ProtectedRoute. */
  role?: string | null;
}

export interface VisibleSection {
  id: NavSectionId;
  section: NavSection;
  items: NavItem[];
}

/** The old AR sidebar's "residential-only audience" (V17.297): an AR Residential role, not an AR Commercial Leader, no users.edit. */
export function isResidentialOnlyAR(u: NavUser): boolean {
  const r = u.roleAssignments;
  return (r.includes('AR Residential Leader') || r.includes('AR Residential Officer'))
    && !r.includes('AR Commercial Leader') && !u.permissions.includes('users.edit');
}

export function canSee(item: NavItem, u: NavUser): boolean {
  const has = (c: string) => u.permissions.includes(c);
  // Menu-only narrowing, ruling 2026-10-26-007 (listed in MENU_NARROWER_THAN_ROUTE).
  if (item.hideFromResidentialOnlyAR && isResidentialOnlyAR(u)) return false;
  // excludeScouts — the exact test ProtectedRoute applies to these routes.
  if (item.excludeScouts && has('commercial.leads.create') && u.role !== 'sales_rep_commercial' && !has('commercial.leads.assign')) {
    return false;
  }
  const p = item.permission;
  let ok: boolean;
  if (p === null) ok = true; // the route needs a signed-in user only
  else if (typeof p === 'string') ok = has(p);
  else if ('any' in p) ok = p.any.some(has);
  else ok = p.all.every(has);
  if (!ok && item.orRoleAssignments) ok = item.orRoleAssignments.some((r) => u.roleAssignments.includes(r));
  // The route's requireRoleAssignment (ruling 2026-10-26-006): the permission AND the role.
  if (ok && item.andRoleAssignment) ok = u.roleAssignments.includes(item.andRoleAssignment);
  return ok;
}

export function visibleSections(u: NavUser): VisibleSection[] {
  return NAV_SECTIONS.map((section) => ({ id: section.id, section, items: section.items.filter((i) => canSee(i, u)) })).filter(
    (s) => s.items.length > 0,
  );
}

/** The path of the active menu entry (Home's '/dashboard' included), or null. */
export function activeItemPath(pathname: string, hash: string, items: readonly NavItem[]): string | null {
  const full = pathname + (hash || '');
  if (hash && items.some((i) => i.path === full)) return full;
  if (pathname === HOME.path) return HOME.path;
  if (items.some((i) => i.path === pathname)) return pathname;
  let best: string | null = null;
  for (const i of items) {
    if (i.path.includes('#')) continue;
    if (pathname.startsWith(`${i.path}/`) && (!best || i.path.length > best.length)) best = i.path;
  }
  return best;
}

export const FIRST_VISIT_ALL_OPEN_MAX = 12;

export function firstVisitOpen(
  sections: readonly VisibleSection[],
  activeSection: NavSectionId | null,
  user?: NavUser,
): NavSectionId[] {
  if (user) {
    const main = sections
      .filter((s) => s.items.some((i) => i.primaryForRoles?.some((r) => user.roleAssignments.includes(r))))
      .map((s) => s.id);
    if (main.length) return main;
  }
  const total = sections.reduce((n, s) => n + s.items.length, 0);
  if (total <= FIRST_VISIT_ALL_OPEN_MAX) return sections.map((s) => s.id);
  return activeSection ? [activeSection] : [];
}

export interface NavState {
  collapsed: boolean;
  open: string[];
}

export const navStateKey = (userId: string) => `vtw.nav.v1.${userId}`;

export function loadNavState(userId: string): NavState | null {
  try {
    const raw = localStorage.getItem(navStateKey(userId));
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<NavState>;
    if (typeof v.collapsed !== 'boolean' || !Array.isArray(v.open) || !v.open.every((x) => typeof x === 'string')) return null;
    return { collapsed: v.collapsed, open: v.open };
  } catch {
    return null;
  }
}

export function saveNavState(userId: string, state: NavState): void {
  try {
    localStorage.setItem(navStateKey(userId), JSON.stringify(state));
  } catch {
    // storage full or blocked — the menu still works, it just will not remember
  }
}

export function searchItems(query: string, items: readonly NavItem[]): NavItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...items];
  return items.filter((i) => i.label.lo.toLowerCase().includes(q) || i.label.en.toLowerCase().includes(q));
}
